import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import PoweredByFooter from "@/components/PoweredByFooter";

/**
 * Prescription print — A4 layout with LARGE blank top and bottom margins so
 * the physical hospital letterhead prints cleanly around the content.
 * Empty clinical sections are hidden.
 */
export default function PrescriptionPrint() {
  const { opdId } = useParams();
  const [sp] = useSearchParams();
  const isPreview = sp.get("preview") === "1";
  const invOnly = sp.get("investigation_only") === "1";
  // Case-sheet mode: skips the typed Rx (medicines/advice/treatment) and the
  // doctor signature, so a visiting doctor can hand-write the prescription
  // in the blank space below the case-sheet header.
  const caseSheet = sp.get("case") === "1";
  const [d, setD] = useState(null);
  const brand = useBranding();
  useEffect(() => { api.get(`/opd/${opdId}`).then((r) => setD(r.data)); }, [opdId]);
  useEffect(() => {
    if (d && !isPreview) {
      // Auto-close the tab after the print dialog is dismissed
      window.onafterprint = () => window.close();
      setTimeout(() => window.print(), 500);
    }
  }, [d, isPreview]);

  useEffect(() => {
    document.documentElement.classList.add("print-mode-a4");
    return () => document.documentElement.classList.remove("print-mode-a4");
  }, []);
  if (!d) return <div className="p-8">Loading...</div>;
  const v = d.vitals || {};
  const has = (x) => x !== undefined && x !== null && String(x).trim() !== "";
  const anyVital = Object.values(v || {}).some((x) => has(x));
  const meds = d.medicines || [];
  const invs = d.investigations || [];
  const procs = d.procedures || [];
  const labs = d.lab_tests || [];
  // Extract the padded serial from the OPD number so the printed slip carries
  // a compact "No. 00001" pill that matches the bill.
  const rxSerial = String(d.opd_no || "").replace(/^OPD/i, "");

  return (
    <div className="min-h-screen bg-slate-100 flex justify-center py-6">
      <div className="print-a4-rx page-a4 shadow-lg" style={{ fontSize: "12px" }}>
        {rxSerial && (
          <div className="flex justify-end -mt-2 mb-1">
            <div className="inline-block border border-red-700 rounded-sm px-2 py-0.5 font-mono text-sm text-red-700 font-semibold">
              No. {rxSerial}
            </div>
          </div>
        )}
        {/* Compact single-line patient identifier (letterhead already carries hospital name). */}
        <div className="grid grid-cols-4 gap-2 border-b border-slate-800 pb-2 text-[11px]">
          <div><b>NAME</b><div className="uppercase">{d.patient_name}</div></div>
          <div><b>UHID / OPD</b><div className="font-mono">{d.uhid} · {d.opd_no}</div></div>
          <div><b>AGE / SEX</b><div>{d.age || "-"} / {d.gender || "-"}</div></div>
          <div><b>DATE</b><div>{new Date(d.created_at).toLocaleDateString("en-IN")}</div></div>
          <div className="col-span-2"><b>DOCTOR</b><div className="uppercase">{d.doctor_name} · {d.department}</div></div>
          {has(d.reference_doctor) && <div className="col-span-2"><b>REF.</b><div className="uppercase">{d.reference_doctor}</div></div>}
          {d.scheme && d.scheme !== "NON_PMJAY" && <div><b>SCHEME</b><div>{d.scheme}{d.pmjay_package_name ? ` · ${d.pmjay_package_name}` : ""}</div></div>}
        </div>

        <div className="mt-3 space-y-3">
          {!invOnly && (has(d.chief_complaint) || has(d.clinical_findings)) && (
            <Section title="CHIEF COMPLAINTS / CLINICAL FINDINGS">
              <div className="uppercase leading-relaxed">
                {[d.chief_complaint, d.clinical_findings].filter(has).join(" — ")}
              </div>
            </Section>
          )}
          {anyVital && (
            <Section title="VITALS">
              <div className="grid grid-cols-7 gap-2 text-center text-[11px]">
                {["bp","pr","rs","temp","spo2","wt","ht"].map((k) => has(v[k]) && (
                  <div key={k} className="border border-dashed py-1">
                    <div className="text-[9px] text-slate-500 uppercase">{k}</div>
                    <div className="font-medium">{v[k]}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}
          {!invOnly && has(d.diagnosis) && <Section title="DIAGNOSIS"><div className="uppercase">{d.diagnosis}</div></Section>}
          {!invOnly && !caseSheet && has(d.treatment) && <Section title="TREATMENT"><div className="uppercase">{d.treatment}</div></Section>}
          {(invs.length > 0 || d.xray_count > 0 || labs.length > 0) && (
            <Section title={invOnly ? "INVESTIGATION REQUISITION" : "INVESTIGATIONS"}>
              <div className="text-[12px] uppercase leading-relaxed">
                {invs.map((name, i) => {
                  const n = (d.investigation_notes || {})[name] || {};
                  const parts = (n.body_parts && n.body_parts.length ? n.body_parts.join("+") : n.body_part) || "";
                  const views = (n.views && n.views.length ? n.views.join("/") : "");
                  const contrast = n.contrast && n.contrast !== "No" ? `w/ ${n.contrast}` : "";
                  const urgency = n.urgency && n.urgency !== "Routine" ? `[${n.urgency}]` : "";
                  const sample = n.sample_type ? `(${n.sample_type})` : "";
                  const fasting = n.fasting ? "[FASTING]" : "";
                  const films = n.films && n.films > 1 ? `× ${n.films}` : "";
                  const detail = [parts, views, contrast, sample, urgency, fasting, films].filter(Boolean).join(" ");
                  const ind = n.indication ? ` — ${n.indication}` : "";
                  return (
                    <div key={`inv-${i}`} className="flex gap-1">
                      <span>•</span>
                      <span><b>{name}</b>{detail && <> — {detail}</>}{ind}</span>
                    </div>
                  );
                })}
                {d.xray_count > 0 && invs.length === 0 && <div>· X-RAY × {d.xray_count}</div>}
                {labs.length > 0 && <div>· LAB: {labs.join(", ")}</div>}
              </div>
            </Section>
          )}
          {!invOnly && (procs.length > 0 || (d.procedure_entries || []).length > 0) && (
            <Section title="PROCEDURE">
              <div className="text-[12px] uppercase leading-relaxed">
                {/* Structured INJECTION / FLUIDS entries */}
                {(d.procedure_entries || []).map((e, i) => {
                  const bits = [e.name];
                  if (e.route) bits.push(`(${e.route})`);
                  const drug = e.drug || e.fluid;
                  if (drug) bits.push(`· ${drug}`);
                  if (e.dose) bits.push(`· ${e.dose}`);
                  if (e.volume) bits.push(`· ${e.volume}`);
                  if (e.note) bits.push(`— ${e.note}`);
                  return (
                    <div key={`pe-${i}`} className="flex gap-1">
                      <span>•</span>
                      <span><b>{bits.join(" ")}</b></span>
                    </div>
                  );
                })}
                {/* Simple procedures that aren't duplicated as structured entries */}
                {procs.filter(p => !["INJECTION", "FLUIDS"].includes(p)).map((p, i) => {
                  const note = (d.procedure_notes || {})[p];
                  return (
                    <div key={`proc-${i}`} className="flex gap-1">
                      <span>•</span>
                      <span><b>{p}</b>{note ? ` — ${note}` : ""}</span>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {!invOnly && !caseSheet && meds.length > 0 && (
            <div>
              <div className="text-[10px] tracking-widest font-semibold text-slate-800 border-b pb-1 mb-1">Rx — MEDICINES</div>
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="border-b border-slate-400">
                    <th className="text-left w-6 pb-1">#</th>
                    <th className="text-left pb-1">Medicine</th>
                    <th className="text-left w-24 pb-1">Dose</th>
                    <th className="text-left w-32 pb-1">Timing</th>
                    <th className="text-left w-20 pb-1">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {meds.map((m, i) => (
                    <tr key={i} className="border-b border-dashed">
                      <td className="py-1.5">{i + 1}</td>
                      <td className="uppercase font-medium">{m.name}</td>
                      <td className="font-mono">{m.dose}</td>
                      <td className="uppercase text-[11px]">{m.food}</td>
                      <td className="uppercase">{m.duration}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!invOnly && !caseSheet && has(d.advice) && <Section title="ADVICE"><div className="uppercase whitespace-pre-line">{d.advice}</div></Section>}

          {/* Case-sheet mode: leave a large blank Rx zone for the visiting
              doctor to hand-write the prescription. Height is capped so the
              signature + Vyomra footer stay on the same A4 page. */}
          {caseSheet && (
            <div style={{ marginTop: 4 }} data-testid="case-blank-rx">
              <div className="text-[10px] tracking-widest font-semibold text-slate-800 border-b pb-0.5 mb-2">
                Rx
              </div>
              <div style={{ position: "relative", minHeight: "125mm", pageBreakInside: "avoid", breakInside: "avoid" }}>
                <div
                  aria-hidden
                  style={{
                    position: "absolute", inset: 0,
                    backgroundImage: "repeating-linear-gradient(to bottom, transparent 0, transparent 10mm, #cbd5e1 10mm, #cbd5e1 10.15mm)",
                    opacity: 0.35,
                  }}
                />
                <div aria-hidden style={{
                  position: "absolute", right: "2mm", top: "0",
                  fontSize: "42px", color: "#cbd5e1", fontStyle: "italic",
                  transform: "translateY(-4mm)",
                }}>℞</div>
              </div>
            </div>
          )}

          <div className="flex justify-between pt-3">
            <div>
              {!invOnly && !caseSheet && has(d.follow_up) && <div className="text-[12px]"><b>FOLLOW-UP:</b> <span className="uppercase">{d.follow_up}</span></div>}
            </div>
            <div className="text-right">
              {caseSheet ? (
                // Empty signature slot — the visiting doctor signs by hand.
                <div className="w-52 flex flex-col items-end">
                  <div className="border-t border-slate-800 pt-1 w-full mt-2">
                    <div className="text-[11px] uppercase font-semibold text-slate-500">Doctor's Signature</div>
                  </div>
                </div>
              ) : (
                <div className="w-52 flex flex-col items-end">
                  {brand.show_doctor_sig_rx && d.doctor_id && (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/doctors/${d.doctor_id}/signature`}
                      alt=""
                      style={{ maxHeight: 50, maxWidth: "100%", objectFit: "contain", marginBottom: 2 }}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  )}
                  <div className={brand.show_doctor_sig_rx ? "border-t border-slate-800 pt-1 w-full" : "pt-1 w-full"}>
                    <div className="text-[11px] uppercase font-semibold">{d.doctor_name}</div>
                    <div className="text-[10px] text-slate-500 uppercase">{d.department}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <PoweredByFooter />
      </div>
      <div className="fixed top-2 right-2 no-print">
        <button onClick={() => window.print()} className="bg-blue-700 text-white px-3 py-1 text-sm rounded-sm">Print</button>
        <div className="text-[10px] mt-1 text-slate-500 bg-white/70 p-1 rounded-sm max-w-[240px]">Print on <b>{brand.name}</b> letterhead paper — top and bottom areas are intentionally blank.</div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-widest font-semibold text-slate-800 border-b pb-0.5 mb-1">{title}</div>
      <div className="pl-1">{children}</div>
    </div>
  );
}
